// @owners { team: patients-team }
import { COLORS, SIZES, SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  Button,
  Column,
  Description,
  H1,
  LgSpacing,
  MdSpacing,
  Row,
  XlSpacing,
  useScreenSize,
} from '@alto/design-system';
import { AddressFormActionSheet } from '@alto/features';
import React, { useContext, useEffect } from 'react';
import styled from 'styled-components';
import { MISSING_ADDRESS } from '~shared/features/essentials/add_ons_consts';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { type Shipment } from '~shared/types';
import { StoreHeaderWrapper } from './StoreHeaderWrapper';
import { useSidePadding } from './useSidePadding';
import { FullPage } from '~web/features/essentials/components';
import { Image } from '~web/features/essentials/primitives';

const StyledContentColumn = styled(Column)<{ padding: string }>`
  padding: ${({ padding }) => padding};
`;

type Props = {
  readonly shipment: Shipment | undefined;
};

export const MissingAddress = ({ shipment }: Props) => {
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const { trackPageView } = useAnalytics();
  const { title, description, image, buttonText } = MISSING_ADDRESS;
  const { isSMScreen } = useScreenSize();
  const isMobile = isSMScreen;
  const sidePadding = useSidePadding();
  const padding = isMobile ? SPACING.STATIC.XL.px : `0 ${sidePadding} 0 ${SPACING.STATIC.XXL.px}`;
  const imageSize = isMobile ? SIZES.ILLUSTRATION.LG.px : SIZES.ILLUSTRATION.XL.px;

  useEffect(() => {
    trackPageView({ event: EVENTS.ESSENTIALS__STORE_NO_ADDRESS_VIEWED });
  }, [trackPageView]);

  const handleAddressPress = () => {
    setActiveActionSheet(<AddressFormActionSheet origin="Store Products" />);
  };

  return (
    <>
      <FullPage>
        <StoreHeaderWrapper shipment={shipment}>
          <XlSpacing />
        </StoreHeaderWrapper>
        <StyledContentColumn
          flexShrink={1}
          flexBasis="65%"
          backgroundColor={COLORS.BACKGROUND_COLORS.WHITE}
          padding={padding}
          {...(isMobile ? {} : { center: true })}
        >
          <Row center>
            <Image
              src={image}
              alt="icon of address location"
              width={imageSize}
              height={imageSize}
            />
          </Row>
          <LgSpacing />
          <H1 center>{title}</H1>
          <MdSpacing />
          <Description center>{description}</Description>
          <XlSpacing />
          <Row center>
            <Button
              label={buttonText}
              onPress={handleAddressPress}
            />
          </Row>
        </StyledContentColumn>
      </FullPage>
    </>
  );
};
